import React, { useMemo } from 'react';
import { format } from 'date-fns';
import type { PlannedSegment } from '../../utils/calendarEventGroupPlan';
import type {
  ConflictResolutionAction,
  TripConflictUnit,
} from '../../utils/calendarTripConflicts';
import {
  buildConflictResolutionPlan,
  canCutout,
  computeCutoutRanges,
  formatCutoutPreview,
} from '../../utils/calendarTripConflicts';
import './TripConflictResolutionPanel.css';

type Props = {
  units: TripConflictUnit[];
  tripEnd: Date;
  travelSegments: PlannedSegment[];
  actions: Record<string, ConflictResolutionAction>;
  onActionChange: (key: string, action: ConflictResolutionAction) => void;
  onBack: () => void;
  onConfirm: () => void;
  confirming: boolean;
};

const TripConflictResolutionPanel: React.FC<Props> = ({
  units,
  tripEnd,
  travelSegments,
  actions,
  onActionChange,
  onBack,
  onConfirm,
  confirming,
}) => {
  const modifiable = units.filter(u => u.canModify);
  const readOnly = units.filter(u => !u.canModify);

  const plan = useMemo(
    () => buildConflictResolutionPlan(units, actions, tripEnd, travelSegments),
    [units, actions, tripEnd, travelSegments],
  );

  const adjustByKey = useMemo(() => {
    const map = new Map<string, { start: Date; end: Date }>();
    for (const op of plan.adjusts) {
      map.set(op.unit.key, { start: op.start, end: op.end });
    }
    return map;
  }, [plan.adjusts]);

  const cutoutPreviewByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const unit of units) {
      if (!canCutout(unit, travelSegments)) continue;
      const ev = unit.events[0];
      if (!ev.start || !ev.end) continue;
      try {
        const start = new Date(ev.start);
        const end = new Date(ev.end);
        const ranges = computeCutoutRanges(start, end, travelSegments);
        map.set(unit.key, formatCutoutPreview(ranges));
      } catch {
        /* ignore */
      }
    }
    return map;
  }, [units, travelSegments]);

  return (
    <div className="eeb-conflicts-step">
      <h4>Scheduling conflicts</h4>
      <p className="eeb-conflicts-intro">
        This trip overlaps {units.length} existing event{units.length === 1 ? '' : 's'}.
        Move events after the trip, trim around travel time (cut out), or delete them.
      </p>

      <ul className="eeb-conflicts-list">
        {units.map(unit => {
          const action = actions[unit.key] || 'adjust';
          const adjustTarget = adjustByKey.get(unit.key);
          const cutoutPreview = cutoutPreviewByKey.get(unit.key);
          const cutoutAllowed = canCutout(unit, travelSegments);
          return (
            <li key={unit.key} className="eeb-conflict-row">
              <div className="eeb-conflict-main">
                <span className="eeb-conflict-title">{unit.title}</span>
                <span className="eeb-conflict-time">
                  {format(unit.start, 'EEE M/d h:mm a')} – {format(unit.end, 'h:mm a')}
                </span>
                {unit.isGrouped && (
                  <span className="eeb-conflict-note">Linked trip — moves or deletes as one unit</span>
                )}
                {!unit.canModify && (
                  <span className="eeb-conflict-note warn">Read-only calendar — cannot change here</span>
                )}
              </div>
              {unit.canModify ? (
                <div className="eeb-conflict-actions">
                  {cutoutAllowed && (
                    <label className={action === 'cutout' ? 'selected' : ''}>
                      <input
                        type="radio"
                        name={`conflict-${unit.key}`}
                        checked={action === 'cutout'}
                        onChange={() => onActionChange(unit.key, 'cutout')}
                      />
                      Cut out travel ({cutoutPreview})
                    </label>
                  )}
                  <label className={action === 'adjust' ? 'selected' : ''}>
                    <input
                      type="radio"
                      name={`conflict-${unit.key}`}
                      checked={action === 'adjust'}
                      onChange={() => onActionChange(unit.key, 'adjust')}
                    />
                    {adjustTarget
                      ? `Move to ${format(adjustTarget.start, 'h:mm a')} – ${format(adjustTarget.end, 'h:mm a')}`
                      : 'Move after trip'}
                  </label>
                  <label className={action === 'delete' ? 'selected' : ''}>
                    <input
                      type="radio"
                      name={`conflict-${unit.key}`}
                      checked={action === 'delete'}
                      onChange={() => onActionChange(unit.key, 'delete')}
                    />
                    Delete {unit.kind === 'group' ? 'linked trip' : 'event'}
                  </label>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {readOnly.length > 0 && (
        <p className="eeb-conflicts-readonly-hint">
          {readOnly.length} conflict{readOnly.length === 1 ? '' : 's'} on read-only calendars will remain;
          adjust those manually after creating the trip.
        </p>
      )}

      <div className="eac-modal-actions">
        <button type="button" onClick={onBack} disabled={confirming}>
          Back
        </button>
        <button
          type="button"
          className="primary"
          disabled={confirming || modifiable.length === 0}
          onClick={() => void onConfirm()}
        >
          {confirming ? 'Creating…' : 'Create trip & resolve conflicts'}
        </button>
      </div>
    </div>
  );
};

export default TripConflictResolutionPanel;
